import { Button } from "@/components/ui/button";
import {useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useNavigate} from "react-router";
import CreateOrganizationModal from "../components/Organizations/CreateOrganizationModal";
import OrganizationCreatedModal from "../components/Organizations/OrganizationCreatedModal";
import {OrganizationWithSecret} from "../models/Organization";
import OrganizationsRepository from "../repositories/OrganizationsRepository";
import { UserRole } from "../models/User";
import ConfirmationModal from "../components/common/ConfirmationModal";
import { OrganizationsTable } from "../components/Organizations/OrganizationsTable";
import { useNotifiedMutation } from "../hooks/useNotifiedMutation";
import { QUERY_KEYS } from "../constants/QueryKeys";

interface CreateOrganization {
  name: string;
  intactId: string;
  secret: string;
}

interface OrganizationsPageProps {
  userRole: UserRole;
  userId: string;
}

const OrganizationsPage: React.FC<OrganizationsPageProps> = ({
  userRole,
  userId
}) => {
  const organizationsRepository = new OrganizationsRepository();
  const navigation = useNavigate();
  const queryClient = useQueryClient();
  const [createdOrganization, setCreatedOrganization] = useState<OrganizationWithSecret | null>(null);
  const [createOrganizationModalOpened, setCreateOrganizationModalOpened] = useState<boolean>(false);
  const [saveChangesModalOpened, setSaveChangesModalOpened] = useState<boolean>(false);
  const [deleteOrganizationId, setDeleteOrganizationId] = useState<string | null>(null);
  const [deleteOrganizationName, setDeleteOrganizationName] = useState<string | null>(null);

  const createOrganizationMutation = useNotifiedMutation({
    mutationKey: [QUERY_KEYS.ORGANIZATIONS],
    mutationFn: async (newOrganization: CreateOrganization) => {
      return await organizationsRepository.createOrganization(
        newOrganization.name,
        newOrganization.intactId,
        newOrganization.secret,
      );
    },
    successMessage: (_, vars) => `Organization "${vars.name}" created`,
    errorMessage: (err) => err?.message ?? "Failed to create organization",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_NAMES] });
    },
  });

  const deleteOrganizationMutation = useNotifiedMutation({
    mutationFn: async (organizationId: string) => {
      await organizationsRepository.deleteOrganization(organizationId);
    },
    successMessage: () =>
      deleteOrganizationName
        ? `Organization "${deleteOrganizationName}" deleted`
        : "Organization deleted",
    errorMessage: (err) => err?.message ?? "Failed to delete organization",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_NAMES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ASSIGNABLE_USERS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
    },
  });

  const handleConfirmDelete = async () => {
    if (deleteOrganizationId) {
      try {
        await deleteOrganizationMutation.mutateAsync(deleteOrganizationId);
      } catch {
        // toast already shown by useNotifiedMutation
      }
    }
    setDeleteOrganizationId(null);
    setDeleteOrganizationName(null);
    setSaveChangesModalOpened(false);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight">Organizations</h2>
        <Button
          disabled={userRole === UserRole.Viewer}
          size="sm"
          onClick={() => setCreateOrganizationModalOpened(true)}
        >
          Create Organization
        </Button>
      </div>

      <OrganizationsTable
        userRole={userRole}
        userId={userId}
        onDeleteOrganization={function (organizationId: string, organizationName: string): void {
          setSaveChangesModalOpened(true);
          setDeleteOrganizationId(organizationId);
          setDeleteOrganizationName(organizationName)
        }}
      />

      <CreateOrganizationModal
        open={createOrganizationModalOpened}
        onClose={function (): void {
          setCreateOrganizationModalOpened(false);
        }}
        onCreateOrganization={async function (
          name: string,
          intactId: string,
          secret: string,
        ): Promise<void> {
          const organization = await createOrganizationMutation.mutateAsync({
            name: name,
            intactId: intactId,
            secret: secret,
          });
          setCreatedOrganization(organization);
        }}
      />

      <OrganizationCreatedModal
        open={createdOrganization !== null}
        onClose={function (): void {
          setCreatedOrganization(null);
        }}
        organization={createdOrganization}
        onGoToOrganization={function (): void {
          navigation(`/organization/${createdOrganization?.id}`);
        }}
      />

      <ConfirmationModal
        open={saveChangesModalOpened}
        onClose={function (): void {
          setDeleteOrganizationId(null);
          setSaveChangesModalOpened(false);
        }}
        onConfirm={async function (): Promise<void> {
          if (deleteOrganizationId) {
            await handleConfirmDelete();
            return;
          }
        }}
        message="Are you sure you want to delete this organization?"
        statusColor="danger"
        buttonText="Delete"
      >
        <div className="flex flex-wrap">
          <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold">
            {deleteOrganizationName}
          </span>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default OrganizationsPage;
